import Component from '@glimmer/component'
import { reportingTitle, reportingToolTip as toolTipConfig } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import type { TooltipOptions } from 'highcharts'
import { BaseHighcharterComponent } from 'teacher-ui-client/components/base/highcharter'
import type * as Highcharts from 'highcharts'

interface Args {
  animation?: boolean
  borderWidth?: number
  categories?: string[]
  chartMargin?: (number | null)[]
  chartSpacing?: number[]
  colorByPoint?: boolean
  colors?: string[]
  data: Highcharts.SeriesBarOptions[]
  groupPadding?: number
  legend?: unknown
  maxPointWidth?: number
  plotBackgroundColor?: string
  plotBands?: unknown
  plotLines?: unknown
  plotOptions?: unknown
  pointPadding?: number
  stackingStyle?: Highcharts.OptionsStackingValue
  tickLength?: number
  title?: string
  toolTip?: TooltipOptions
  toolTipFormatter?: Highcharts.TooltipFormatterCallbackFunction
  toolTipUnit?: string
  xAxis?: Highcharts.XAxisOptions | Highcharts.XAxisOptions[]
  xAxisLabel?: Highcharts.XAxisTitleOptions | string
  xAxisLabelFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction
  xAxisVisible?: boolean
  yAxis?: Highcharts.YAxisOptions | Highcharts.YAxisOptions[]
  yAxisGridLineColor?: string
  yAxisGridLineWidth?: number
  yAxisLabel?: string
  yAxisLabelFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction
  yAxisLineWidth?: number
  yAxisOffset?: number
  yAxisOpposite?: boolean
  yAxisReversedStacks?: boolean
  yAxisTickInterval?: number
  yAxisVisible?: boolean
  yRange?: number[]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class SeriesBar extends Component<Signature> {
  get chartMargins() {
    if (!this.args.chartMargin) return { margin: undefined }

    const [marginTop, marginRight, marginBottom, marginLeft] = this.args.chartMargin

    return {
      marginTop: marginTop ?? undefined,
      marginRight: marginRight ?? undefined,
      marginBottom: marginBottom ?? undefined,
      marginLeft: marginLeft ?? undefined,
    }
  }

  get borderWidth() {
    return this.args.borderWidth ?? 0
  }

  get categories() {
    return this.args.categories ?? null
  }

  get chartMargin() {
    return this.args.chartMargin
  }

  get chartOptions(): Highcharts.Options {
    return {
      chart: {
        type: 'bar',
        plotBackgroundColor: this.plotBackgroundColor,
        backgroundColor: 'transparent',
        spacing: this.chartSpacing,
        ...this.chartMargins,
      },
      credits: { enabled: false },
      title: this.title,
      yAxis: this.yAxis,
      xAxis: this.xAxis,
      colors: this.colors,
      plotOptions: this.plotOptions,
      tooltip: this.toolTip,
      legend: this.legend,
    }
  }

  get chartSpacing() {
    return this.args.chartSpacing ?? [10, 10, 10, 10]
  }

  get colorByPoint() {
    return this.args.colorByPoint ?? false
  }

  get colors() {
    return this.args.colors
  }

  get groupPadding() {
    return this.args.groupPadding ?? 0.2
  }

  get highchartsOptions(): Highcharts.Options {
    return { ...this.chartOptions, series: this.args.data }
  }

  get legend() {
    return this.args.legend ?? { enabled: false }
  }

  get maxPointWidth() {
    return this.args.maxPointWidth
  }

  get plotBackgroundColor() {
    return this.args.plotBackgroundColor ?? colors.dustyBlack50
  }

  get plotBands() {
    return this.args.plotBands ?? []
  }

  get plotLines() {
    return this.args.plotLines ?? []
  }

  get plotOptions(): Highcharts.PlotOptions {
    if (this.args.plotOptions) return this.args.plotOptions

    return {
      bar: {
        borderWidth: this.borderWidth,
        stacking: this.args.stackingStyle,
        animation: this.args.animation,
        groupPadding: this.groupPadding,
        pointPadding: this.pointPadding,
        colorByPoint: this.colorByPoint,
        maxPointWidth: this.maxPointWidth,
      },
    }
  }

  get pointPadding() {
    return this.args.pointPadding ?? 0.1
  }

  get title() {
    const title = this.args.title === undefined ? '' : this.args.title
    if (typeof title === 'string') {
      return reportingTitle(title)
    }
    return title
  }

  get toolTip() {
    if (this.args.toolTip) return this.args.toolTip

    const formatter = this.args.toolTipFormatter

    return toolTipConfig('y', this.toolTipUnit, { formatter, backgroundColor: colors.neutral400 })
  }

  get toolTipUnit() {
    return this.args.toolTipUnit ?? ''
  }

  get reversedStacks() {
    return this.args.yAxisReversedStacks ?? true
  }

  get tickLength() {
    return this.args.tickLength ?? 10
  }

  get xAxis() {
    if (this.args.xAxis) return this.args.xAxis

    const xAxis: Record<string, unknown> = {
      title: this.xAxisLabel,
      categories: this.categories,
      visible: this.xAxisVisible,
      tickLength: this.tickLength,
      allowDecimals: false,
      lineColor: colors.dustyBlack200,
    }

    if (this.args.xAxisLabelFormatter) {
      xAxis.labels = {
        formatter: this.args.xAxisLabelFormatter,
      }
    }

    return xAxis
  }

  get xAxisLabel() {
    if (typeof this.args.xAxisLabel === 'string') return reportingTitle(this.args.xAxisLabel)

    return this.args.xAxisLabel
  }

  get xAxisVisible() {
    return this.args.xAxisVisible ?? true
  }

  get yAxis() {
    if (this.args.yAxis) return this.args.yAxis

    const [min, max] = this.yRange

    const yAxis: Record<string, unknown> = {
      title: reportingTitle(this.yAxisLabel),
      visible: this.yAxisVisible,
      min,
      max,
      plotLines: this.plotLines,
      plotBands: this.plotBands,
      allowDecimals: false,
      offset: this.yAxisOffset,
      opposite: this.yAxisOpposite,
      reversedStacks: this.reversedStacks,
      lineWidth: this.yAxisLineWidth,
      gridLineWidth: this.yAxisGridLineWidth,
      gridLineColor: this.yAxisGridLineColor,
      tickInterval: this.args.yAxisTickInterval,
    }

    if (this.args.yAxisLabelFormatter) {
      yAxis.labels = {
        formatter: this.args.yAxisLabelFormatter,
      }
    }

    return yAxis
  }

  get yAxisGridLineColor() {
    return this.args.yAxisGridLineColor ?? colors.dustyBlack100
  }

  get yAxisGridLineWidth() {
    return this.args.yAxisGridLineWidth ?? 1
  }

  get yAxisLabel() {
    return this.args.yAxisLabel ?? ''
  }

  get yAxisLineWidth() {
    return this.args.yAxisLineWidth ?? 0
  }

  get yAxisOffset() {
    return this.args.yAxisOffset ?? 0
  }

  get yAxisOpposite() {
    return this.args.yAxisOpposite ?? false
  }

  get yAxisVisible() {
    return this.args.yAxisVisible ?? true
  }

  get yRange() {
    return this.args.yRange ?? []
  }

  <template><BaseHighcharterComponent class="h-full" @options={{this.highchartsOptions}} ...attributes /></template>
}

export default SeriesBar
